import { useParams, useSearchParams } from "#routers/hooks";
import WorkflowCommentsDrawer from "#src/batteries-included-components/Drawers/Workflows/WorkflowCommentsDrawer/WorkflowCommentsDrawer";
import { WorkflowFormSubmissionsTablePanel } from "#src/batteries-included-components/Panels/TablePanels/WorkflowFormSubmissionsTablePanel";
import { WorkflowStatusToPillVariantMap } from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { WorkflowTasksTablePanel } from "#src/batteries-included-components/Panels/TablePanels/WorkflowTasksTablePanel";
import { useGetOneUser } from "#src/components/hooks/adapters/useUsers";
import { useGetOneWorkflow } from "#src/components/hooks/adapters/useWorkflows";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import {
  useCheckCommentPermissions,
  useCommentsStore,
} from "#src/components/hooks/useComments";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  Banner,
  Button,
  ButtonToggleGroup,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  TimelinePanel,
  toast,
  Tooltip,
  usePrevious,
  useTimelinePanelStore,
} from "@validereinc/common-components";
import {
  ResourceDefinitions,
  WorkflowStatus,
  type WorkflowStatusType,
} from "@validereinc/domain";
import { dateFormatter, toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { Titled } from "react-titled";
import { WorkflowDetailsRoutePath } from ".";
import { WorkflowDetailDeleteDialog } from "./WorkflowDetailDeleteDialog";
import { WorkflowDetailKeyValuePanel } from "./WorkflowDetailKeyValuePanel";
import styles from "./WorkflowDetailPage.module.scss";
import { WorkflowDetailStatusDialog } from "./WorkflowDetailStatusDialog";
import { WorkflowTriggerKeyValuePanel } from "./WorkflowTriggerKeyValuePanel";

const cx = classNames.bind(styles);

const WORKFLOW_COMMENTS_DRAWER_KEY = "workflow-comments";

type PageContextType = "actions" | "submissions";

export const WorkflowDetailPage = () => {
  const { workflowId } = useParams<{
    workflowId: string;
  }>();
  const [
    tasksFilterConfigStorageKey,
    tasksTableConfigStorageKey,
    tasksViewConfigStorageKey,
    tasksTableStateStorageKey,
    tasksGroupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    "workflows-detail-tasks"
  );
  const [
    formsFilterConfigStorageKey,
    formsTableConfigStorageKey,
    formsTableStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
    ],
    "workflows-detail-forms"
  );
  const {
    isOpen: isWorkflowCommentsDrawerOpen,
    setIsOpen: setIsWorkflowCommentsDrawerOpen,
  } = useTimelinePanelStore();
  const { commentCounts } = useCommentsStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);

  // The type will translate into: {task_id?: string, context?: PageContextType}
  const [searchParams, setSearchParams] = useSearchParams<
    Omit<
      Exclude<typeof WorkflowDetailsRoutePath.queryParams, undefined>,
      "tab"
    > & { context: PageContextType }
  >();

  const { context, task_id } = searchParams;

  const setContext = (newContext: PageContextType) => {
    setSearchParams({
      ...searchParams,
      context: newContext,
    });
  };

  useEffect(() => {
    if (context) return;
    setContext("actions");
  }, [context]);

  const workflowQuery = useGetOneWorkflow(
    { id: workflowId },
    {
      enabled: Boolean(workflowId),
      refetchInterval: (data) => {
        if (
          !!data?.data.status &&
          (
            [
              WorkflowStatus.IN_PROGRESS,
              WorkflowStatus.OVERDUE,
            ] as WorkflowStatusType[]
          ).includes(data?.data.status)
        ) {
          return 4 * 1000;
        } else {
          return false;
        }
      },
    }
  );
  const workflow = useMemo(() => workflowQuery.data?.data, [workflowQuery]);
  const prevWorkflow = usePrevious(workflow);
  const { canReadComments, canReadAssetType } = useCheckCommentPermissions(
    workflow?.asset?.asset_type
  );
  const { workflow_template } = workflow ?? {};

  const workflowIsLoading = workflowQuery.isLoading;

  const isDismissed = workflow?.status === WorkflowStatus.DISMISSED;
  const { data: dismissedByUser } = useGetOneUser({
    id: isDismissed ? (workflow?.dismissed_by ?? null) : null,
  });

  const createInfo = useActionDetails({
    type: "create",
    date: workflow?.created_at,
    byUserId: undefined,
  });
  const updateInfo = useActionDetails({
    type: "update",
    date: workflow?.updated_at,
    byUserId: workflow?.updated_by,
  });
  const dueDateInfo = useActionDetails({
    type: "due",
    date: workflow?.due_date
      ? typeof workflow.due_date === "string"
        ? workflow.due_date
        : new Date(workflow.due_date).toISOString()
      : undefined,
    byUserId: undefined,
  });
  const metaSegments = [
    dueDateInfo.description,
    updateInfo.description,
    createInfo.description,
  ].filter((s) => s !== undefined);

  const [breadcrumbs] = useBreadcrumbsFromRoute(WorkflowDetailsRoutePath, {
    "/detail": {
      title: `${workflow?.name} - ${workflow?.id}`,
    },
  });

  useEffect(() => {
    if (
      prevWorkflow?.status &&
      workflow?.status &&
      prevWorkflow?.status !== WorkflowStatus.COMPLETE &&
      workflow?.status === WorkflowStatus.COMPLETE
    ) {
      toast.push({
        intent: "success",
        description:
          "Successfully completed all workflow tasks and completed the workflow.",
      });
    }
  }, [workflow?.status]);

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleChangeStatus = () => {
    setIsStatusModalOpen(true);
  };

  useEffect(() => {
    setIsWorkflowCommentsDrawerOpen(false);
  }, []);

  const commentCount = commentCounts[`workflowComments-${workflowId}`] || 0;

  return (
    <Titled
      title={generateDocumentTitle(
        breadcrumbs.at(-1)?.title ?? WorkflowDetailsRoutePath.title,
        breadcrumbs.at(-2)?.title ?? ResourceDefinitions.workflow.label.plural
      )}
    >
      <Page
        title={
          workflow ? (
            <>
              {workflow.name}&nbsp;
              <Pill
                variant={
                  WorkflowStatusToPillVariantMap[workflow.status] ?? "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(workflow.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            WorkflowDetailsRoutePath.title
          )
        }
        category={WorkflowDetailsRoutePath.subtitle}
        breadcrumbs={breadcrumbs}
        isLoading={workflowIsLoading}
        actionRow={[
          <Button
            key="secondary"
            variant="error-outline"
            onClick={handleDelete}
          >
            Delete
          </Button>,
          ...(canReadComments && canReadAssetType
            ? [
                <TimelinePanel.Trigger
                  key="comments-button"
                  targetDrawer={WORKFLOW_COMMENTS_DRAWER_KEY}
                >
                  {({ onClick }) => (
                    <Button
                      className={cx("commentsButton", {
                        "commentsButton--active": isWorkflowCommentsDrawerOpen,
                      })}
                      onClick={onClick}
                      slotLeft={
                        <Icon
                          variant="chat-text"
                          size={24}
                        />
                      }
                      slotRight={<Pill variant="default">{commentCount}</Pill>}
                    >
                      Comments
                    </Button>
                  )}
                </TimelinePanel.Trigger>,
              ]
            : []),
          workflow?.status === WorkflowStatus.DISMISSED ? (
            <Tooltip
              key="primary"
              content="Cannot change the status of a dismissed workflow"
            >
              <Button
                variant="primary"
                disabled
                onClick={handleChangeStatus}
              >
                Change Status
              </Button>
            </Tooltip>
          ) : (
            <Button
              key="primary"
              variant="primary"
              onClick={handleChangeStatus}
            >
              Change Status
            </Button>
          ),
        ]}
        renderMeta={
          metaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => <MetaSegments values={metaSegments} />
        }
        {...(workflow
          ? {
              pageRightSlot: (
                <WorkflowCommentsDrawer
                  workflow={workflow}
                  drawerKey={WORKFLOW_COMMENTS_DRAWER_KEY}
                />
              ),
            }
          : {})}
      >
        {isDismissed ? (
          <Banner
            variant="warning"
            titleText="This workflow has been dismissed"
            description={
              [
                workflow?.dismissal_reason,
                dismissedByUser?.name
                  ? `By ${dismissedByUser.name}`
                  : undefined,
                workflow?.updated_at
                  ? `On ${dateFormatter(new Date(workflow.updated_at), false)}`
                  : undefined,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            className={cx("dismissalBanner")}
          />
        ) : null}
        <div>
          <div>
            <WorkflowDetailKeyValuePanel workflowDetails={workflow} />
            {workflow?.asset_trigger ||
            workflow?.workflow_trigger ||
            workflow?.form_trigger ? (
              <WorkflowTriggerKeyValuePanel workflowDetails={workflow} />
            ) : null}
          </div>
        </div>
        <div className={cx("filterContainer")}>
          <ButtonToggleGroup
            name="workflow-details-switcher"
            className={cx("contentSwitch")}
            items={[
              { label: "Actions", dataKey: "actions" },
              { label: "Form Submissions", dataKey: "submissions" },
            ]}
            activeKey={context ?? ""}
            onChange={(newStatus: string) =>
              setContext(newStatus as PageContextType)
            }
          />
        </div>
        <div>
          {context === "actions" ? (
            <WorkflowTasksTablePanel
              filterConfigStorageKey={tasksFilterConfigStorageKey}
              tableConfigStorageKey={tasksTableConfigStorageKey}
              viewConfigStorageKey={tasksViewConfigStorageKey}
              tableStateStorageKey={tasksTableStateStorageKey}
              groupByTablesStateStorageKey={tasksGroupByTablesStateStorageKey}
              isWithinWorkflowDetail
              isDisplayingActions
              taskFilters={{ workflow_id: workflowId }}
              // When there's a task_id in the URL, that task will be open automatically:
              defaultTaskId={task_id}
              // When a task gets selected, task_id is inserted into the URL:
              onTaskSelection={(id) => {
                setSearchParams(
                  produce(searchParams, (draft) => {
                    if (id) draft.task_id = id;
                    else delete draft.task_id;
                  })
                );
              }}
            />
          ) : (
            <WorkflowFormSubmissionsTablePanel
              filterConfigStorageKey={formsFilterConfigStorageKey}
              tableConfigStorageKey={formsTableConfigStorageKey}
              tableStateStorageKey={formsTableStateStorageKey}
              workflowId={workflowId}
            />
          )}
        </div>
      </Page>

      {workflow_template ? (
        <WorkflowDetailDeleteDialog
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          isDeleteModalOpen={isDeleteModalOpen}
          workflowId={workflowId}
          workflowCategoryId={workflow_template.workflow_category.id}
          workflowTemplateId={workflow_template.id}
        />
      ) : null}

      {workflow ? (
        <>
          <WorkflowDetailStatusDialog
            setIsStatusModalOpen={setIsStatusModalOpen}
            isStatusModalOpen={isStatusModalOpen}
            workflowDetails={workflow}
          />
        </>
      ) : null}
    </Titled>
  );
};
